import type { FlowNodeType } from '@reflex/flows';

/**
 * Canvas card chrome from the Reflex Figma node specs:
 * sizes 240 / 280 / 320, type-colored tags, hover border, selected ring.
 * Grouped the way the design system groups them: Start, Work, Agents,
 * Flow control.
 */
export const FLOW_NODE_LABELS: Record<FlowNodeType, string> = {
  trigger: 'Trigger',
  action: 'Action',
  llm: 'LLM',
  agent: 'Agent',
  agent_task: 'Agent Task',
  filter: 'Filter',
  router: 'Router',
  approval: 'Approval',
};

export type FlowNodeCardSize = 'sm' | 'md' | 'lg';

export const FLOW_NODE_SIZE: Record<FlowNodeType, FlowNodeCardSize> = {
  trigger: 'sm',
  filter: 'sm',
  router: 'sm',
  approval: 'sm',
  action: 'md',
  llm: 'md',
  agent: 'lg',
  agent_task: 'lg',
};

export const FLOW_NODE_SIZE_CLASS: Record<FlowNodeCardSize, string> = {
  sm: 'w-[240px]',
  md: 'w-[280px]',
  lg: 'w-[320px]',
};

export interface FlowNodeTheme {
  /** Pill background + foreground for the type tag. */
  tag: string;
  /** 1px type-colored border on hover. */
  hover: string;
  /** 1px type-colored border while running / waiting (same hue as hover). */
  active: string;
  /** 1px type-colored border + 4px tint ring when selected. */
  selected: string;
}

const start: FlowNodeTheme = {
  tag: 'bg-[#fcf6ec] text-[#8a5717] dark:bg-[#8a5717]/20 dark:text-[#e8c48a]',
  hover: 'hover:border-[#8a5717] dark:hover:border-[#e8c48a]',
  active: 'border-[#8a5717] dark:border-[#e8c48a]',
  selected:
    'border-[#8a5717] shadow-[0px_0px_0px_4px_#eee5db,0px_6px_16px_0px_rgba(0,0,0,0.08)] dark:border-[#e8c48a] dark:shadow-[0px_0px_0px_4px_rgba(138,87,23,0.35),0px_6px_16px_0px_rgba(0,0,0,0.08)]',
};

const work: FlowNodeTheme = {
  tag: 'bg-[#eeedff] text-[#534bb7] dark:bg-[#534bb7]/25 dark:text-[#c5c0ff]',
  hover: 'hover:border-[#534bb7] dark:hover:border-[#c5c0ff]',
  active: 'border-[#534bb7] dark:border-[#c5c0ff]',
  selected:
    'border-[#534bb7] shadow-[0px_0px_0px_4px_#edecff,0px_6px_16px_0px_rgba(0,0,0,0.08)] dark:border-[#c5c0ff] dark:shadow-[0px_0px_0px_4px_rgba(83,75,183,0.35),0px_6px_16px_0px_rgba(0,0,0,0.08)]',
};

const agents: FlowNodeTheme = {
  tag: 'bg-[#e6f1fb] text-[#195fa5] dark:bg-[#195fa5]/25 dark:text-[#8ec8f5]',
  hover: 'hover:border-[#195fa5] dark:hover:border-[#8ec8f5]',
  active: 'border-[#195fa5] dark:border-[#8ec8f5]',
  selected:
    'border-[#195fa5] shadow-[0px_0px_0px_4px_#e8f4ff,0px_6px_16px_0px_rgba(0,0,0,0.08)] dark:border-[#8ec8f5] dark:shadow-[0px_0px_0px_4px_rgba(25,95,165,0.35),0px_6px_16px_0px_rgba(0,0,0,0.08)]',
};

const flowControl: FlowNodeTheme = {
  tag: 'bg-[#faede7] text-[#993c1e] dark:bg-[#993c1e]/25 dark:text-[#e8b4a0]',
  hover: 'hover:border-[#993c1e] dark:hover:border-[#e8b4a0]',
  active: 'border-[#993c1e] dark:border-[#e8b4a0]',
  selected:
    'border-[#993c1e] shadow-[0px_0px_0px_4px_#ffece6,0px_6px_16px_0px_rgba(0,0,0,0.08)] dark:border-[#e8b4a0] dark:shadow-[0px_0px_0px_4px_rgba(153,60,30,0.35),0px_6px_16px_0px_rgba(0,0,0,0.08)]',
};

export const FLOW_NODE_THEME: Record<FlowNodeType, FlowNodeTheme> = {
  trigger: start,
  action: work,
  llm: work,
  agent: agents,
  agent_task: agents,
  filter: flowControl,
  router: flowControl,
  approval: flowControl,
};
